import React, { useState, useEffect } from "react";
import Modal from "react-modal";
import "./UserManagementPopup.css";
import "./UserManagementCreatePopup.css";
import InputContainer from "../../../../components/Util/InputContainer";
import Swal from "sweetalert2";
import CommonTable from "../components/CommonTable";
import { formatAccessWbar } from "../../../../components/Util/DateFormat";
import { getOAuthIcon } from "../getOAuthIcon";

function UserModifyPopup({ isOpen, onClose, onSave, selectedUser, onDelete, deleteOAuthProvider }) {
  // 초기 사용자 데이터 상태
  const initialUserData = {
    id: "",
    username: selectedUser ? selectedUser.username : "",
    nickname: selectedUser ? selectedUser.nickname : "",
    birthDate: selectedUser ? selectedUser.birthDate : "",
    profileImg: selectedUser ? selectedUser.profileImg : "",
    gender: selectedUser ? selectedUser.gender : "",
    telNumber: selectedUser ? selectedUser.telNumber : "",
    roleName: selectedUser ? selectedUser.roleName : "",
    patientList: selectedUser ? selectedUser.patientList : [],
    oauthList: selectedUser ? selectedUser.oauthList : [],
    lastLoginAt: selectedUser ? selectedUser.lastLoginAt : null,
    createdAt: selectedUser ? selectedUser.createdAt : null,
  };

  // 사용자 데이터 상태와 관련된 상태 변수 및 함수
  const [userData, setUserData] = useState({ ...selectedUser });
  const [usernameValidationMessage, setUsernameValidationMessage] = useState("");
  const [nicknameValidationMessage, setnicknameValidationMessage] = useState("");

  const roleSelectOptions = [
    { label: "시스템관리자", value: 1 },
    { label: "병원관리자", value: 2 },
    { label: "의사", value: 3 },
    { label: "간호사", value: 4 },
  ];

  if (selectedUser && selectedUser.roleId === 5) {
    roleSelectOptions.push({ label: "환자", value: 5 });
  }

  // 생년월일 형식 포맷팅 함수 추가
  const formatBirthDate = (birthDate) => {
    if (!birthDate) return "";

    // 이미 하이픈이 있는 경우 그대로 반환
    if (birthDate.includes("-")) return birthDate;

    // 8자리 숫자인 경우 YYYY-MM-DD 형식으로 변환
    if (birthDate.length === 8) {
      return `${birthDate.substring(0, 4)}-${birthDate.substring(4, 6)}-${birthDate.substring(6, 8)}`;
    }

    return birthDate; // 다른 형식은 그대로 반환
  };

  // 입력값 변경 핸들러
  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === "birthDate") {
      // 사용자 입력 시 숫자와 하이픈만 허용
      const filteredValue = value.replace(/[^\d-]/g, "");
      // 입력된 값에서 숫자만 추출
      const numericValue = filteredValue.replace(/\D/g, "");

      // 최대 8자리까지만 허용 (YYYYMMDD)
      if (numericValue.length <= 8) {
        let formattedValue = numericValue;

        // 자동 포맷팅: YYYY-MM-DD
        if (numericValue.length > 4) {
          formattedValue = `${numericValue.substring(0, 4)}-${numericValue.substring(4, Math.min(6, numericValue.length))}`;
        }

        if (numericValue.length > 6) {
          formattedValue = `${numericValue.substring(0, 4)}-${numericValue.substring(4, 6)}-${numericValue.substring(6, 8)}`;
        }

        setUserData({
          ...userData,
          [name]: formattedValue,
        });
      }
    } else if (name === "telNumber") {
      // 휴대폰 번호 형식(000-0000-0000)만 허용
      const rawValue = value.replace(/[^0-9]/g, "");
      let formattedValue = "";

      // 최대 11자리까지만 입력 허용
      if (rawValue.length <= 11) {
        // 휴대폰 번호 형식(010, 011, 016, 017, 018, 019로 시작)
        if (rawValue.startsWith("010") || rawValue.startsWith("011") || rawValue.startsWith("016") || rawValue.startsWith("017") || rawValue.startsWith("018") || rawValue.startsWith("019")) {
          // 자동 포맷팅
          if (rawValue.length <= 3) {
            formattedValue = rawValue;
          } else if (rawValue.length <= 7) {
            formattedValue = `${rawValue.substring(0, 3)}-${rawValue.substring(3)}`;
          } else {
            formattedValue = `${rawValue.substring(0, 3)}-${rawValue.substring(3, 7)}-${rawValue.substring(7)}`;
          }

          setUserData({
            ...userData,
            [name]: formattedValue,
          });
        } else if (rawValue.length <= 3) {
          // 아직 3자리 미만이면 일단 입력 허용 (010 등의 시작 번호를 입력 중일 수 있음)
          setUserData({
            ...userData,
            [name]: rawValue,
          });
        }
      }
    } else if (name === "newPassword" || name === "confirmPassword") {
      // 비밀번호 필드에서 공백 제거
      const noSpaceValue = value.replace(/\s/g, "");
      setUserData({
        ...userData,
        [name]: noSpaceValue,
      });
    } else if (name === "nickname") {
      setnicknameValidationMessage("");
      if (value.length <= 20) {
        setUserData({
          ...userData,
          [name]: value,
        });
      }
    } else {
      // 다른 필드는 기존 방식으로 처리
      setUserData({
        ...userData,
        [name]: value,
      });
    }
  };

  // 전화번호 형식 포맷팅 함수
  const formatPhoneNumber = (phoneNumber) => {
    if (!phoneNumber) return "";

    // 이미 하이픈이 있는 경우 제거
    const cleaned = phoneNumber.replace(/-/g, "");

    if (cleaned.length === 11) {
      return `${cleaned.substring(0, 3)}-${cleaned.substring(3, 7)}-${cleaned.substring(7)}`;
    } else if (cleaned.length === 10) {
      return `${cleaned.substring(0, 3)}-${cleaned.substring(3, 6)}-${cleaned.substring(6)}`;
    }
    return cleaned; // 포맷팅할 수 없는 경우 원래 값 반환
  };

  // 팝업이 열릴 때 초기 데이터 설정
  useEffect(() => {
    if (isOpen) {
      setUserData({
        id: selectedUser ? selectedUser.id : "",
        username: selectedUser ? selectedUser.username : "",
        nickname: selectedUser ? selectedUser.nickname : "",
        birthDate: selectedUser ? selectedUser.birthDate : "",
        profileImg: selectedUser ? selectedUser.profileImg : "",
        gender: selectedUser ? selectedUser.gender : "",
        telNumber: selectedUser ? selectedUser.telNumber : "",
        roleName: selectedUser ? selectedUser.roleName : "",
        patientList: selectedUser ? selectedUser.patientList : [],
        oauthList: selectedUser ? selectedUser.oauthList : [],
        lastLoginAt: selectedUser ? selectedUser.lastLoginAt : null,
        createdAt: selectedUser ? selectedUser.createdAt : null,
      });
      setUsernameValidationMessage("");
      setnicknameValidationMessage("");
    }
  }, [isOpen, selectedUser]);

  const handleUsernameBlur = () => {
    /// 최소 1자, 최대 20자
    /// 특수문자 및 숫자 입력 제한 (한글/영문만 허용)
    /// 전체 공백 또는 맨앞/맨뒤 공백만 입력된 값은 허용하지 않음
    /// 이름 중간에 한 칸 이내의 공백만 허용 가능
    const username = userData.username?.trim() || "";
    const regex = /^[a-zA-Z가-힣]+$|^[a-zA-Z가-힣]+ [a-zA-Z가-힣]+$/; // 한글, 영어, 공백(중간에 한 칸 이내)만 허용
    if (username.length === 0) {
      setUsernameValidationMessage("사용자명을 입력해주세요.");
      return;
    } else if (!regex.test(username)) {
      setUsernameValidationMessage("한글, 영어만 입력할 수 있어요. (중간에 한 칸 이내의 공백 허용)");
      return;
    } else if (username.length < 1 || username.length > 20) {
      setUsernameValidationMessage("사용자명은 1자 이상 20자 이하로 입력해주세요.");
      return;
    } else {
      setUsernameValidationMessage("");
    }
  };

  const handleNicknameBlur = () => {
    //이전값과 동일한 경우 패스
    if (initialUserData.nickname === userData.nickname || (initialUserData.nickname === null && userData.nickname === "")) {
      return;
    }

    /// 최소 1자, 최대 20자
    /// 특수문자 및 숫자 입력 제한 (한글/영문만 허용)
    /// 전체 공백 또는 맨앞/맨뒤 공백만 입력된 값은 허용하지 않음
    /// 이름 중간에 한 칸 이내의 공백만 허용 가능
    const nickname = userData.nickname?.trim() || "";
    const regex = /^[a-zA-Z가-힣]+$|^[a-zA-Z가-힣]+ [a-zA-Z가-힣]+$/; // 한글, 영어, 공백(중간에 한 칸 이내)만 허용

    if (nickname.length === 0) {
      setnicknameValidationMessage("닉네임을 입력해주세요.");
      return;
    } else if (!regex.test(nickname)) {
      setnicknameValidationMessage("한글, 영어만 입력할 수 있어요. (중간에 한 칸 이내의 공백 허용)");
      return;
    } else if (nickname.length < 2 || nickname.length > 20) {
      setnicknameValidationMessage("닉네임은 2자 이상 20자 이하로 입력해주세요.");
      return;
    } else {
      setnicknameValidationMessage("");
    }
  };

  // 저장 버튼 클릭 이벤트 핸들러
  const handleSaveClick = async () => {
    if (nicknameValidationMessage !== "" || (initialUserData.nickname && userData.nickname === "")) {
      Swal.fire("알림", "닉네임을 입력해주세요.", "warning");
      return;
    }

    if (usernameValidationMessage !== "" || (initialUserData.username && userData.username === "")) {
      Swal.fire("알림", "사용자명을 입력해주세요.", "warning");
      return;
    }

    if (initialUserData.birthDate && userData.birthDate === "") {
      Swal.fire("알림", "생년월일을 입력해주세요.", "warning");
      return;
    }

    if (initialUserData.telNumber && userData.telNumber === "") {
      Swal.fire("알림", "휴대폰 번호를 입력해주세요.", "warning");
      return;
    }

    // 생년월일 형식 검증
    if (userData.birthDate.trim().length > 0) {
      const birthDateWithoutHyphens = userData.birthDate.replace(/-/g, "");

      if (birthDateWithoutHyphens.length !== 8) {
        Swal.fire("알림", "생년월일 형식이 올바르지 않습니다. (예: YYYY-MM-DD)", "warning");
        return;
      }
    }

    // 전화번호 형식 검증
    if (selectedUser && selectedUser.roleId === 5 && userData.telNumber) {
      // 휴대폰 번호 형식만 체크 (010-0000-0000 등)
      const mobileRegex = /^01[0|1|6|7|8|9]-\d{3,4}-\d{4}$/;
      if (!mobileRegex.test(userData.telNumber)) {
        Swal.fire("알림", "휴대폰 번호 형식이 올바르지 않습니다. (예: 010-0000-0000)", "warning");
        return;
      }
    }

    // 생년월일이 하이픈 형식이 아니라면 하이픈 추가
    let formattedBirthDate = userData.birthDate || undefined;
    if (formattedBirthDate && !formattedBirthDate.includes("-")) {
      formattedBirthDate = formatBirthDate(formattedBirthDate);
    }

    // 전화번호가 하이픈 형식이 아니라면 하이픈 추가
    let formattedTelNumber = userData.telNumber || undefined;
    if (formattedTelNumber && !formattedTelNumber.includes("-")) {
      formattedTelNumber = formatPhoneNumber(formattedTelNumber);
    }

    // 성별 기존에 있었다가 없애는 경우 에러처리
    if (initialUserData.gender && !userData.gender) {
      console.log(userData.gender, initialUserData.gender);
      Swal.fire("알림", "성별을 선택해주세요.", "warning");
      return;
    }

    // 변경된 값만 추출 (생년월일, 전화번호는 포맷 적용)
    const changedFields = {};
    Object.entries(userData).forEach(([key, value]) => {
      let compareValue = value;
      if (key === "birthDate") compareValue = formattedBirthDate;
      if (key === "telNumber") compareValue = formattedTelNumber;
      if (compareValue !== initialUserData[key]) {
        changedFields[key] = compareValue;
      }
    });

    // 변경된 값이 없으면 서버에 요청하지 않음
    if (Object.keys(changedFields).length === 0) {
      Swal.fire("알림", "수정된 내용이 없습니다.", "info");
      return;
    }
    // Ask for confirmation before saving user edits
    const confirmationResult = await Swal.fire({
      icon: "question",
      title: "사용자 수정 저장",
      text: "사용자를 수정하시겠습니까?",
      showCancelButton: true, // Add cancel button
      confirmButtonText: "확인", // Confirm button text
      cancelButtonText: "취소", // Cancel button text
    });

    if (confirmationResult.isConfirmed) {
      try {
        await onSave(changedFields);
      } catch (error) {
        console.error("Error during user update:", error);
      }
    }
  };

  // 연동 병원 리스트 column
  const hospitalColumns = [
    {
      key: "hospitalName",
      label: "병원명",
      textAlign: "center",
    },
    {
      key: "createdAt",
      label: "연동 날짜",
      textAlign: "center",
      render: (row) => formatAccessWbar(row.createdAt),
    },
  ];

  //인증 수단 리스트 column
  const authMethodColumns = [
    {
      key: "provider",
      label: "인증 수단",
      render: (row) => {
        return (
          <div style={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
            {row.provider}
            {getOAuthIcon(row.provider)}
          </div>
        );
      },
    },
    { key: "registeredAt", label: "등록 날짜", render: (row) => formatAccessWbar(row.registeredAt) },
    {
      key: "lastLogin",
      label: "최근 로그인\n수단",
      render: (row) => {
        return <span style={{ display: "block", width: "100%", textAlign: "center" }}>{row.lastLogin ? "O" : ""}</span>;
      },
    },
    {
      key: "action",
      label: "관리",
      isButton: true,
      buttonLabel: "삭제",
    },
  ];

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={onClose}
      contentLabel="User Popup"
      style={{
        overlay: {
          backgroundColor: "rgba(0, 0, 0, 0.5)",
          zIndex: 1000,
        },
        content: {
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          border: "1px solid #ccc",
          padding: "15px",
          background: "white",
          borderRadius: "4px",
          width: "400px",
          height: "658px",
        },
      }}
    >
      <div className="modal-header-close-button" onClick={onClose}>
        x
      </div>
      {userData.id && (
        <div className="patient-user-modal-content">
          <div className="user-modal-header">
            <h2>사용자 상세정보</h2>
          </div>
          <div className="user-tie-container">
            <InputContainer
              inputStyle={{ width: "170px" }}
              autoComplete="off"
              label="사용자명"
              name="username"
              value={userData.username}
              onChange={handleChange}
              validationMessage={usernameValidationMessage === "" ? <span>&nbsp;</span> : usernameValidationMessage}
              onBlur={handleUsernameBlur}
              validationColor={"red"}
            />
            {/* 닉네임 */}
            <InputContainer
              autoComplete="off"
              label="사용자 닉네임"
              name="nickname"
              value={userData.nickname ?? ""}
              onChange={handleChange}
              onBlur={handleNicknameBlur}
              validationMessage={nicknameValidationMessage === "" ? <span>&nbsp;</span> : nicknameValidationMessage}
              validationColor={"red"}
            />
          </div>
          <div className="user-tie-container">
            <div className="input-container">
              <div className="input-row" style={{ display: "flex", flexDirection: "column" }}>
                <label htmlFor="userBirth">생년월일</label>
                <input autoComplete="off" type="text" name="birthDate" value={userData.birthDate} onChange={handleChange} placeholder="YYYY-MM-DD" maxLength={10} />
              </div>
            </div>
            {/* 성별 */}
            <InputContainer
              label="성별"
              name="gender"
              value={userData.gender}
              onChange={handleChange}
              type="select"
              options={[
                { label: "성별", value: "" },
                { label: "여성", value: "F" },
                { label: "남성", value: "M" },
              ]}
            />
            {/* <div className="input-container">
            <div className="input-row" style={{ display: "flex", flexDirection: "column" }}>
              <label htmlFor="userGender">성별</label>

              <input
                autoComplete="off"
                type="text"
                name="userGender"
                value={userData.gender === "F" ? "여성" : userData.gender === "M" ? "남성" : "-"}
                readOnly={true}
                style={{ backgroundColor: "#f0f0f0" }}
              />
            </div>
          </div> */}
          </div>
          <div className="input-container">
            <div className="input-row" style={{ display: "flex", flexDirection: "column", marginTop: "10px" }}>
              <label htmlFor="userTelNo">휴대폰 번호</label>
              <input autoComplete="off" type="text" name="telNumber" value={userData.telNumber} onChange={handleChange} placeholder="010-0000-0000" />
            </div>
          </div>
          <div style={{ width: "100%", margin: "10px 0" }}>
            <p style={{ fontWeight: "400", fontSize: "14px", marginBottom: "5px" }}>연동된 병원 목록</p>
            <CommonTable columns={hospitalColumns} data={userData?.patientList} onButtonClick={(row) => alert(`${row.name} 연동 해지!`)} />
          </div>
          <div style={{ width: "100%", margin: "10px 0" }}>
            <p style={{ fontWeight: "400", fontSize: "14px", marginBottom: "5px" }}>인증 수단 목록</p>
            <CommonTable columns={authMethodColumns} data={userData?.oauthList ?? []} onButtonClick={(row) => deleteOAuthProvider(selectedUser.id, row.id)} />
          </div>
          <div className="user-button-container">
            <button className="dashboard-content-button popup-button" onClick={handleSaveClick}>
              수정
            </button>
            <button
              className="dashboard-content-button red-button popup-button"
              onClick={() => {
                onDelete(selectedUser.id);
              }}
            >
              탈퇴
            </button>
          </div>
        </div>
      )}
    </Modal>
  );
}

export default UserModifyPopup;
